import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, TextField, MenuItem, Button, Chip, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";

import {
  useAdminListHelpArticlesQuery,
  useDeleteHelpArticleMutation,
  usePublishHelpArticleMutation,
  useUnpublishHelpArticleMutation,
  useLazyExportHelpArticlesQuery,
  useImportHelpArticlesMutation,
  useListHelpVersionsQuery,
  useRestoreHelpVersionMutation,
} from "../../../Api/helpApi";
import useUser from "../../../Core/hook/useUser";
import { Privileges } from "../../../Core/Enum/enum";
import useNotifier from "../../../Core/Notifier/use-notifier";
import { useConfirm } from "../../../Components/Confirm/ConfirmProvider";

const HelpManage: React.FC = () => {
  const navigate = useNavigate();
  const { userHasRight } = useUser();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();

  const canCreate = userHasRight(Privileges.HelpArticleCreate);
  const canEdit = userHasRight(Privileges.HelpArticleEdit);
  const canDelete = userHasRight(Privileges.HelpArticleDelete);
  const canPublish = userHasRight(Privileges.HelpArticlePublish);

  const [module, setModule] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState<"" | "published" | "draft">("");
  const [search, setSearch] = useState("");
  const [versionsFor, setVersionsFor] = useState<number | null>(null);

  const query = useMemo(() => ({
    module: module || undefined,
    category: category || undefined,
    status: status || undefined,
    search: search.trim() || undefined,
  }), [module, category, status, search]);

  const { data, isLoading, refetch } = useAdminListHelpArticlesQuery(query);
  const items = data?.data?.items ?? [];

  const [del] = useDeleteHelpArticleMutation();
  const [pub] = usePublishHelpArticleMutation();
  const [unpub] = useUnpublishHelpArticleMutation();
  const [triggerExport] = useLazyExportHelpArticlesQuery();
  const [importArticles] = useImportHelpArticlesMutation();

  const distinctModules = useMemo(() => Array.from(new Set(items.map((i) => i.module))).sort(), [items]);
  const distinctCategories = useMemo(() => Array.from(new Set(items.map((i) => i.category))).sort(), [items]);

  const handleDelete = async (id: number, title: string) => {
    const ok = await confirm({ title: "Delete article?", message: `Delete "${title}"? This cannot be undone.`, variant: "danger" as any });
    if (!ok) return;
    try { await del(id).unwrap(); showMessage("Deleted."); } catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed to delete."); }
  };

  const handlePublish = async (id: number, isPublished: boolean) => {
    try {
      if (isPublished) await unpub(id).unwrap(); else await pub(id).unwrap();
      showMessage(isPublished ? "Unpublished." : "Published.");
    } catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed."); }
  };

  const handleExport = async () => {
    try {
      const res = await triggerExport().unwrap();
      const blob = new Blob([JSON.stringify(res.data?.articles ?? [], null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `help-articles-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e: any) { showErrorMessage(e?.data?.message ?? "Export failed."); }
  };

  const handleImport = async () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "application/json";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      const text = await f.text();
      try {
        const parsed = JSON.parse(text);
        const articles = Array.isArray(parsed) ? parsed : parsed.articles;
        if (!Array.isArray(articles)) { showErrorMessage("Invalid JSON — expected array or {articles:[]}."); return; }
        const ok = await confirm({ title: "Import articles", message: `Import ${articles.length} article(s)? Existing published articles will be SKIPPED unless you confirm overwrite.`, variant: "default" as any });
        if (!ok) return;
        const overwriteOk = await confirm({ title: "Overwrite published?", message: "Also overwrite already-published articles?", variant: "danger" as any });
        const res = await importArticles({ articles, overwrite_published: !!overwriteOk }).unwrap();
        showMessage(`Created ${res.data?.created}, updated ${res.data?.updated}, skipped ${res.data?.skipped}.`);
        refetch();
      } catch (e: any) { showErrorMessage(e?.data?.message ?? e?.message ?? "Import failed."); }
    };
    input.click();
  };

  return (
    <Box sx={{ pb: 6, maxWidth: 1240, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Manage Documentation</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Create, edit, publish and version help articles.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>Export</Button>
          {(canCreate || canEdit) && <Button startIcon={<FileUploadOutlinedIcon />} onClick={handleImport} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>Import</Button>}
          {canCreate && <Button startIcon={<AddIcon />} onClick={() => navigate("/help-center/manage/new")} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>New Article</Button>}
        </Stack>
      </Stack>

      <Card sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField label="Search" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 2 }} />
          <TextField label="Module" size="small" select value={module} onChange={(e) => setModule(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            {distinctModules.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField label="Category" size="small" select value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All</MenuItem>
            {distinctCategories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="Status" size="small" select value={status} onChange={(e) => setStatus(e.target.value as any)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </TextField>
        </Stack>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : items.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>No articles match these filters.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Route</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Media</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{a.title}</TableCell>
                  <TableCell><Chip size="small" label={a.module} variant="outlined" /></TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>{a.route_pattern || "—"}</Typography></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ color: "text.disabled" }}>
                      {a.has_quick && <Tooltip title="Quick steps"><FormatListNumberedRoundedIcon fontSize="small" /></Tooltip>}
                      {a.has_images && <Tooltip title="Screenshots"><PhotoLibraryOutlinedIcon fontSize="small" /></Tooltip>}
                      {a.has_video && <Tooltip title="Video"><PlayCircleOutlineIcon fontSize="small" /></Tooltip>}
                      {a.has_full && <Tooltip title="Full guide"><ArticleOutlinedIcon fontSize="small" /></Tooltip>}
                      {!a.has_quick && !a.has_images && !a.has_video && !a.has_full && <Typography variant="caption">—</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {a.is_published
                      ? <Chip size="small" label="Published" color="success" />
                      : <Chip size="small" label="Draft" color="warning" variant="outlined" />}
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: "text.secondary" }}>{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview"><IconButton size="small" onClick={() => navigate(`/help-center/${a.slug}`)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                    {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/help-center/manage/${a.id}`)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                    {canPublish && (
                      <Tooltip title={a.is_published ? "Unpublish" : "Publish"}>
                        <IconButton size="small" onClick={() => handlePublish(a.id, a.is_published)}>
                          {a.is_published ? <UnpublishedOutlinedIcon fontSize="small" /> : <PublishRoundedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && <Tooltip title="Versions"><IconButton size="small" onClick={() => setVersionsFor(a.id)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>}
                    {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(a.id, a.title)}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {versionsFor !== null && (
        <VersionsDialog id={versionsFor} onClose={() => setVersionsFor(null)} />
      )}
    </Box>
  );
};

const VersionsDialog: React.FC<{ id: number; onClose: () => void }> = ({ id, onClose }) => {
  const { data, isLoading } = useListHelpVersionsQuery(id);
  const [restore] = useRestoreHelpVersionMutation();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const versions = data?.data?.versions ?? [];

  const handleRestore = async (v: number) => {
    const ok = await confirm({ title: `Restore version ${v}?`, message: "A new version will be recorded — history is preserved.", variant: "default" as any });
    if (!ok) return;
    try { await restore({ id, version: v }).unwrap(); showMessage("Restored."); onClose(); } catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed."); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Version history</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={20} /></Stack>
        ) : versions.length === 0 ? (
          <Alert severity="info">No versions recorded yet.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Change</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((v) => (
                <TableRow key={v.id}>
                  <TableCell><Chip size="small" label={`v${v.version_number}`} /></TableCell>
                  <TableCell>{v.change_summary ?? "—"}</TableCell>
                  <TableCell><Typography variant="caption">{v.created_at ? new Date(v.created_at).toLocaleString() : "—"}</Typography></TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => handleRestore(v.version_number)} sx={{ textTransform: "none", fontWeight: 700 }}>Restore</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpManage;
